import { describeApiError, type FantasyContest, type FantasyDriftTeam } from '@brush/api-client';
import { ErrorNotice, Spinner, formatPoints } from '@brush/ui';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import {
  DRIFT_ADOPT_EXPLANATION,
  DRIFT_HOLD_EXPLANATION,
  pausedNotice,
  signed,
} from '../../fantasy/contest';
import { useAdoptFantasyScope, useFantasyContestDrift } from '../../queries';
import { modalInput } from '../Modal';

/**
 * §6.2's scope drift, as the decision it is: what changed, what adopting would cost, and the one
 * control that resolves it.
 *
 * <b>Administrator-only, and asked for only when it can say something.</b> The endpoint is
 * `AuthPolicies.Admin`, so an operator's screen firing it would carry a permanent 403 notice — which
 * reads as a broken app rather than as a report they do not have. The same call the leaderboard's
 * own drift indicator already makes. A contest that has not locked has frozen nothing to have
 * drifted from, so it is not asked for there either.
 *
 * <b>Hold has no button, and the panel says so rather than leaving a gap.</b> Hold is what is
 * already happening — the projection skips a drifted contest — and it is §6.2's default precisely
 * because it needs nobody to have decided anything. A control confirming the status quo would leave
 * the contest exactly as it was while reading as though something had been settled, which is worse
 * than no control. So the two outcomes are stated side by side and only one of them is a button.
 *
 * <b>The teams table is the point of the panel.</b> Base spec §8.1 asks for affected rows and point
 * deltas beside a drift alert, and the reason applies here with more force: "three events were
 * added" is not a basis on which anybody can decide whether to rescore a competition somebody has
 * already entered. Every team is listed, including the ones whose points do not move — an entrant
 * whose own total is unchanged can still lose a place to one whose is not.
 *
 * <b>Nothing here is computed.</b> Every figure is the server's, from the projection that Adopt goes
 * on to freeze. A browser subtracting one column from another would be a second answer to the
 * question this screen exists to answer, free to disagree with what actually gets written.
 */
export function ContestDriftPanel({
  contest,
  roomId,
  isAdmin,
}: {
  contest: FantasyContest;
  /** The room whose queries are invalidated after adopting. Null while none is selected. */
  roomId: string | null;
  /** Whether the signed-in caller may read the report and make the decision. Both are admin-only. */
  isAdmin: boolean;
}) {
  const locked = contest.status === 'locked';
  const query = useFantasyContestDrift(contest.id, isAdmin && locked);

  const adopt = useAdoptFantasyScope(roomId);

  const [reason, setReason] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [showTeams, setShowTeams] = useState(false);

  // An operator sees the pause on the contest header and is told where the answer lives; there is
  // nothing for them here, and an empty bordered box would read as something that failed to load.
  if (!isAdmin || !locked) return null;

  if (query.isLoading) {
    return (
      <div className="mb-4 border border-line bg-surface p-4">
        <Spinner label="Checking the event list…" />
      </div>
    );
  }

  // A failed drift read is not worth a notice of its own. It reports on a contest that is either
  // scoring normally or already visibly paused, and the pause is surfaced by the contest itself.
  if (query.isError || !query.data?.hasDrift) return null;

  const drift = query.data;
  const trimmed = reason.trim();

  return (
    <div className="mb-4 border border-chip-500/40 bg-chip-500/5 p-4">
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <span className="flex items-center gap-1.5 text-[13px] font-semibold text-ink-950">
          <AlertTriangle className="size-4 text-chip-500" />
          This contest’s event list changed after it locked
        </span>
        <span className="num text-[11px] text-ink-800/60">
          {drift.added.length > 0
            ? `${drift.added.length} added`
            : null}
          {drift.added.length > 0 && drift.removed.length > 0 ? ' · ' : null}
          {drift.removed.length > 0 ? `${drift.removed.length} removed` : null}
        </span>
      </div>

      <p className="mt-1.5 text-[12px] leading-relaxed text-ink-800/70">
        {pausedNotice(contest.standingsPausedAt)}
      </p>

      <EventList label="Added to the leaderboard" events={drift.added} />
      <EventList label="No longer scored" events={drift.removed} />

      {/* §8.1's affected rows. Behind a disclosure because the decision above it is readable
          without them and a contest with fifty entries would otherwise bury it — but the summary
          line stays visible, since "how much would this move" is the question being weighed. */}
      {drift.teams.length > 0 ? (
        <div className="mt-3 border-t border-chip-500/25 pt-2.5">
          <button
            type="button"
            onClick={() => setShowTeams((x) => !x)}
            aria-expanded={showTeams}
            className="flex items-center gap-1 text-[11.5px] font-medium text-ink-800/75 hover:text-ink-950"
          >
            {showTeams ? <ChevronDown className="size-3" /> : <ChevronRight className="size-3" />}
            {showTeams ? 'Hide what adopting would do' : 'See what adopting would do'}
            <span className="ml-1 num font-normal text-ink-800/50">
              {drift.teams.length} team{drift.teams.length === 1 ? '' : 's'}
            </span>
          </button>

          {showTeams ? <TeamTable teams={drift.teams} /> : null}
        </div>
      ) : null}

      {/* §6.2's two outcomes, side by side, and only one of them is a control. */}
      <div className="mt-3.5 grid gap-3 border-t border-chip-500/25 pt-3 sm:grid-cols-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/55">
            Hold — what is happening now
          </p>
          <p className="mt-1 text-[11.5px] leading-relaxed text-ink-800/70">
            {DRIFT_HOLD_EXPLANATION}
          </p>
        </div>

        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/55">
            Adopt the new list
          </p>
          <p className="mt-1 text-[11.5px] leading-relaxed text-ink-800/70">
            {DRIFT_ADOPT_EXPLANATION}
          </p>

          {/* The reason box is here before the attempt, not after the refusal. The server requires
              it and would refuse without it, but discovering that by being refused makes the audit
              trail feel like an obstacle rather than the point of the operation. Same call as the
              leaderboard's closed-board recalculation. */}
          <label className="mt-2 block">
            <span className="mb-1 block text-[11px] text-ink-800/60">
              Why are you adopting the new event list?
            </span>
            <input
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setConfirming(false);
              }}
              placeholder="e.g. Event 12 was added to the series and the room agreed it counts"
              className={modalInput}
            />
          </label>

          <button
            type="button"
            disabled={trimmed.length === 0 || adopt.isPending}
            onClick={() => {
              if (!confirming) {
                setConfirming(true);
                return;
              }

              adopt.mutate(
                { id: contest.id, reason: trimmed },
                {
                  onSuccess: () => {
                    setReason('');
                    setConfirming(false);
                  },
                },
              );
            }}
            className="mt-2 border border-chip-500/50 px-3 py-2 text-[13px] text-ink-900 transition hover:bg-chip-500/10 disabled:opacity-40"
          >
            {adopt.isPending
              ? 'Adopting…'
              : confirming
                ? 'Confirm — rescore under the new list'
                : 'Adopt and rescore'}
          </button>

          {confirming && !adopt.isPending ? (
            <p className="mt-1.5 text-[11px] leading-relaxed text-ink-800/60">
              {/* Two clicks, and the second states the consequence — room-web's rule for every
                  destructive action. What it mostly says is what does *not* happen. */}
              Every roster is rescored. The standings entrants have already seen are kept and stay
              readable, and this contest will say it was amended after lock.
            </p>
          ) : null}

          <ErrorNotice message={describeApiError(adopt.error)} className="mt-2" />
        </div>
      </div>
    </div>
  );
}

/** One side of the diff, or nothing at all — a heading over an empty list says less than silence. */
function EventList({
  label,
  events,
}: {
  label: string;
  events: readonly { eventId: string; internalId: string; name: string; date: string }[];
}) {
  if (events.length === 0) return null;

  return (
    <div className="mt-2.5">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-ink-800/55">
        {label}
      </p>
      <ul className="mt-1 space-y-0.5">
        {events.map((event) => (
          <li key={event.eventId} className="text-[12px] text-ink-900">
            {/* The room's own reference first: an operator matches this against their paperwork,
                and the generated id names nothing to them. */}
            <span className="num text-ink-800/60">{event.internalId}</span> {event.name}
            <span className="num ml-2 text-ink-800/45">{event.date}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * What adopting would do, per roster.
 *
 * Sorted by how far each team would move, largest first, so the rosters the decision most affects
 * are the ones read. Not filtered to them: a team standing still while the one below it climbs past
 * is affected exactly as much, and it is the pair that makes the consequence legible.
 */
function TeamTable({ teams }: { teams: readonly FantasyDriftTeam[] }) {
  const ordered = [...teams].sort(
    (a, b) => Math.abs(b.pointsDelta ?? 0) - Math.abs(a.pointsDelta ?? 0),
  );

  return (
    <div className="mt-2 overflow-x-auto">
      <table className="w-full min-w-[28rem] text-left text-[12px]">
        <thead className="text-[10px] uppercase tracking-[0.08em] text-ink-800/45">
          <tr className="border-b border-chip-500/25">
            <th className="py-1.5 pr-2 font-semibold">Team</th>
            <th className="px-2 py-1.5 text-right font-semibold">Now</th>
            <th className="px-2 py-1.5 text-right font-semibold">Would be</th>
            <th className="px-2 py-1.5 text-right font-semibold">Points</th>
            <th className="py-1.5 pl-2 text-right font-semibold">Place</th>
          </tr>
        </thead>
        <tbody>
          {ordered.map((team) => (
            <tr key={team.teamId} className="border-b border-chip-500/15 last:border-b-0">
              <td className="py-1 pr-2 text-ink-950">{team.teamName}</td>

              {/* Null is not zero: a contest whose board drifted before it was ever projected has
                  no current figure, and printing 0.00 there would state a score nobody has. */}
              <td className="num px-2 py-1 text-right text-ink-800/60">
                {team.currentPoints === null
                  ? '—'
                  : `${formatPoints(team.currentPoints)} · ${team.currentRank}`}
              </td>
              <td className="num px-2 py-1 text-right text-ink-950">
                {formatPoints(team.projectedPoints)} · {team.projectedRank}
              </td>
              <td
                className={`num px-2 py-1 text-right ${
                  (team.pointsDelta ?? 0) === 0 ? 'text-ink-800/40' : 'font-semibold text-ink-950'
                }`}
              >
                {team.pointsDelta === null ? '—' : signed(team.pointsDelta, 2)}
              </td>
              <td
                className={`num py-1 pl-2 text-right ${
                  (team.rankDelta ?? 0) === 0 ? 'text-ink-800/40' : 'font-semibold text-ink-950'
                }`}
              >
                {/* Already positive-is-up on the wire, so nothing here flips a sign. */}
                {team.rankDelta === null ? '—' : signed(team.rankDelta)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className="mt-1.5 text-[11px] leading-relaxed text-ink-800/55">
        Every figure here is the server’s, from the same projection adopting would freeze — not an
        estimate. “Place” is the team’s rank, and “+2” means it would move two places up the board.
      </p>
    </div>
  );
}
